class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // Add color mapping dictionary
  static getColorMapping() {
    return {
      // French to English color mappings
      'Noir': 'Black',
      'Blanc': 'White',
      'Rouge': 'Red',
      'Bleu': 'Blue',
      'Vert': 'Green',
      'Jaune': 'Yellow',
      'Orange': 'Orange',
      'Rose': 'Pink',
      'Violet': 'Purple',
      'Gris': 'Gray',
      'Brun': 'Brown',
      'Beige': 'Beige',
      'Marine': 'Navy',
      'Marron': 'Brown',
      'Argent': 'Silver',
      'Or': 'Gold',
      'Crème': 'Cream',
      'Kaki': 'Khaki',
      'Turquoise': 'Turquoise',
      'Bourgogne': 'Burgundy',
      'Charcoal': 'Charcoal',
      'Tan': 'Tan',
      'Olive': 'Olive'
      // Add more color mappings as needed
    };
  }

  // Method to normalize color parameters in search params
  static normalizeColorParams(searchParams) {
    const colorMapping = FacetFiltersForm.getColorMapping();
    const params = new URLSearchParams(searchParams);
    
    // Get all color filter parameters
    const colorParams = [];
    for (const [key, value] of params.entries()) {
      if (key === 'filter.v.option.color' || key.includes('color')) {
        colorParams.push({ key, value });
      }
    }
    
    // Replace French colors with English equivalents
    colorParams.forEach(({ key, value }) => {
      if (colorMapping[value]) {
        params.delete(key);
        params.append(key, colorMapping[value]);
      }
    });
    
    return params.toString();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const filterSidebarState = document.getElementById('ProductGridContainer').querySelector('.collection-vertical-container').hasAttribute('open');
    
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    if(filterSidebarState){
      document.getElementById('ProductGridContainer').querySelector('.collection-vertical-container').setAttribute('open', '')
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // Normalize color parameters before updating URL
    const normalizedParams = FacetFiltersForm.normalizeColorParams(searchParams);
    history.pushState({ searchParams: normalizedParams }, '', `${window.location.pathname}${normalizedParams && '?'.concat(normalizedParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    // Normalize color parameters before rendering
    const normalizedParams = FacetFiltersForm.normalizeColorParams(searchParams);
    FacetFiltersForm.renderPage(normalizedParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    // Normalize color parameters when removing filters
    const normalizedUrl = FacetFiltersForm.normalizeColorParams(url);
    FacetFiltersForm.renderPage(normalizedUrl);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetFiltersTrigger extends HTMLElement {
  constructor() {
    super();
    this.querySelector('.js-facets-filter-trigger').addEventListener('click', this.onActiveFilterClick.bind(this));
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const verticalWrap = document.querySelector('.collection-vertical-container');
    const buttonLabel = this.querySelector('.button-label');

    if (buttonLabel.innerHTML === "Filters") {
      buttonLabel.innerHTML = "Hide Filters";
    } else {
      buttonLabel.innerHTML = "Filters";
    }

    if(!verticalWrap) return false;

    
    if(verticalWrap.classList.contains('compare-triggered')){
      verticalWrap.classList.remove('compare-triggered');
      verticalWrap.classList.add('filter-triggered');
    }else{
      verticalWrap.toggleAttribute('open');
      verticalWrap.classList.contains('filter-triggered') ? verticalWrap.classList.remove('filter-triggered'):verticalWrap.classList.add('filter-triggered');
    }
  }
}

customElements.define('facet-filters-trigger', FacetFiltersTrigger);

class FacetCompareTrigger extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', this.onActiveFilterClick.bind(this));
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const verticalWrap = document.querySelector('.collection-vertical-container');
    const productCards = document.querySelectorAll('.card-wrapper');

    if(!verticalWrap) return false;

    if(verticalWrap.classList.contains('filter-triggered')){
      verticalWrap.classList.remove('filter-triggered');
      verticalWrap.classList.add('compare-triggered');
    }else{
      verticalWrap.toggleAttribute('open');
      verticalWrap.classList.contains('compare-triggered') ? verticalWrap.classList.remove('compare-triggered') : verticalWrap.classList.add('compare-triggered');
    }

    if(productCards.length > 0){
      productCards.forEach(function(productCard){
        productCard.classList.contains('compare-card-cell') ? productCard.classList.remove('compare-card-cell') : productCard.classList.add('compare-card-cell');
      })
    }
  }
}

customElements.define('facet-compare-trigger', FacetCompareTrigger);




class FacetCompareTriggerMobile extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', this.onActiveFilterClick.bind(this));
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const verticalWrap = document.querySelector('.collection-vertical-container');
    const productCards = document.querySelectorAll('.card-wrapper');

    if(!verticalWrap) return false;

    if(verticalWrap.classList.contains('filter-triggered')){
      verticalWrap.classList.remove('filter-triggered');
      verticalWrap.classList.add('compare-triggered');
    }else{
      verticalWrap.toggleAttribute('open');
      verticalWrap.classList.contains('compare-triggered') ? verticalWrap.classList.remove('compare-triggered') : verticalWrap.classList.add('compare-triggered');
    }

    if(productCards.length > 0){
      productCards.forEach(function(productCard){
        productCard.classList.contains('compare-card-cell') ? productCard.classList.remove('compare-card-cell') : productCard.classList.add('compare-card-cell');
      })
    }
  }
}

customElements.define('mobile-facet-compare', FacetCompareTriggerMobile);



class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class CardComparision extends HTMLElement {
  constructor() {
    super();
    const compareButton = this.querySelector('button');
    compareButton.addEventListener('click', this.comparisonAdd.bind(this));
  }

  comparisonAdd(event) {
    event.preventDefault();
    const button = event.target;
    const productHandle = button.getAttribute('data-product-handle');
    const productImage = button.getAttribute('data-product-image');
    const productTitle = button.getAttribute('data-product-title');
    const productPrice = button.getAttribute('data-product-price');
    const compareListWrap = document.querySelector('.js-comparison-list');
    const compareCell = `<div class="compare-cell">
      <div class="compare-cell-image">
        <img
          src="${productImage}"
          alt="${productTitle}"
          loading="lazy"
          width="100"
          height="100">
      </div>
      <div class="compare-cell-content">
        <h3 class="compare-cell-title">${productTitle}</h3>
        <div class="compare-cell-price">${productPrice}</div>
      </div>
    </div>`;
    const parentCard = this.closest('.compare-card-cell');

    // add selected item to localstorage
    const addedItem = {
      title: productTitle,
      price: productPrice,
      featuredImage: productImage,
      handle: productHandle
    };
    const addedHandle = productHandle;
    var recentAddedItems = window.localStorage.getItem('recentAddedItems')?JSON.parse(window.localStorage.getItem('recentAddedItems')):{};    
    const addedItemcount = Object.entries(recentAddedItems).length;

    if(addedItemcount < 3){
      compareListWrap.insertAdjacentHTML('beforeend', compareCell);
      parentCard.classList.add('added');
      button.innerText = 'ADDED';

      recentAddedItems[addedHandle] = addedItem;
      window.localStorage.setItem('recentAddedItems', JSON.stringify(recentAddedItems));
    }
  }
}

customElements.define('card-comparision', CardComparision);